'use client'

import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { RecipientLevel } from '@/lib/usaspending/recipientId'

import type { RecipientProfile } from '@/types/usaspending'

/**
 * Chart ref registry for PDF export capture
 * Note: topAgencies is rendered as a table, not captured as an image
 */
export interface ChartRefRegistry {
  spendingTrend?: HTMLElement | null
  awardMix?: HTMLElement | null
  geography?: HTMLElement | null
}

// Highlight timing constants (hoisted for stability)
const HIGHLIGHT_SCROLL_DELAY = 650 // Delay when scrolling to section
const HIGHLIGHT_DURATION = 1200 // How long highlight stays visible
const TABS_HEIGHT = 44 // Height of minimap tabs for scroll offset

/**
 * RecipientDetailContext
 *
 * Provides recipient data to all child components via context.
 * Following Vercel composition patterns:
 * - State lifted into provider
 * - Decoupled from implementation (how data is fetched)
 */

export interface RecipientDetailContextValue {
  /** Recipient id ({hash}-{level}) */
  recipientId: string
  /** Recipient hash (ID) */
  recipientHash: string
  /** Recipient level parsed from the URL (P, C, or R) */
  recipientLevel: RecipientLevel
  /** Basic profile data (if available) */
  profile?: RecipientProfile
  /** Register a chart element for PDF export capture */
  registerChartRef: (
    key: keyof ChartRefRegistry,
    element: HTMLElement | null,
  ) => void
  /** Chart refs registry */
  chartRefs: React.RefObject<ChartRefRegistry>
  /** Currently highlighted section ID (for scroll-to highlight effect) */
  highlightedSectionId: string | null
  /** Scroll to a section and apply highlight effect */
  scrollToSection: (sectionId: string) => void
  /** Register scroll container ref for scroll calculations */
  setScrollContainer: (element: HTMLElement | null) => void
  /** Whether a programmatic scroll is in progress (for IntersectionObserver coordination) */
  isProgrammaticScrolling: React.RefObject<boolean>
}

export const RecipientDetailContext =
  createContext<RecipientDetailContextValue | null>(null)

export function useRecipientDetail(): RecipientDetailContextValue {
  const context = useContext(RecipientDetailContext)
  if (!context) {
    throw new Error(
      'useRecipientDetail must be used within a RecipientDetailProvider',
    )
  }
  return context
}

export interface RecipientDetailProviderProps {
  /** Recipient id ({hash}-{level}) */
  recipientId: string
  /** Pre-loaded profile data (optional, for SSR) */
  initialProfile?: RecipientProfile
  children: ReactNode
}

export function RecipientDetailProvider({
  recipientId,
  initialProfile,
  children,
}: RecipientDetailProviderProps) {
  const { recipientHash, recipientLevel } = useMemo(() => {
    if (!recipientId) return { recipientHash: '', recipientLevel: 'R' as const }
    const lastDash = recipientId.lastIndexOf('-')
    if (lastDash <= 0)
      return { recipientHash: recipientId, recipientLevel: 'R' as const }
    const maybeLevel = recipientId.slice(lastDash + 1).toUpperCase()
    if (
      maybeLevel.length === 1 &&
      (['P', 'C', 'R'] as readonly string[]).includes(maybeLevel)
    ) {
      return {
        recipientHash: recipientId.slice(0, lastDash),
        recipientLevel: maybeLevel as RecipientLevel,
      }
    }
    return { recipientHash: recipientId, recipientLevel: 'R' as const }
  }, [recipientId])
  const chartRefs = useRef<ChartRefRegistry>({})
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Lock to prevent IntersectionObserver from updating active section during programmatic scroll
  const isProgrammaticScrolling = useRef(false)

  // Highlight state - which section is currently highlighted
  const [highlightedSectionId, setHighlightedSectionId] = useState<
    string | null
  >(null)

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    }
  }, [])

  const registerChartRef = useCallback(
    (key: keyof ChartRefRegistry, element: HTMLElement | null) => {
      chartRefs.current[key] = element
    },
    [],
  )

  const setScrollContainer = useCallback((element: HTMLElement | null) => {
    scrollContainerRef.current = element
  }, [])

  /**
   * Scroll to a section and apply highlight effect.
   * Follows the useScrollHighlight pattern:
   * - Immediate highlight if already in view
   * - Delayed highlight if scrolling needed
   * - Auto-remove after duration
   *
   * Sets isProgrammaticScrolling during scroll to coordinate with IntersectionObserver.
   */
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId)
    const scrollContainer = scrollContainerRef.current

    // Clear any pending highlight timeout
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current)
      highlightTimeoutRef.current = null
    }

    if (!element) return

    // Set scroll lock to prevent IntersectionObserver from changing active section mid-scroll
    isProgrammaticScrolling.current = true

    // Check if element is already in view (cache rect values)
    const elementRect = element.getBoundingClientRect()
    const containerRect = scrollContainer?.getBoundingClientRect()
    const viewportTop = containerRect?.top ?? 0
    const viewportBottom = containerRect?.bottom ?? window.innerHeight
    const isInView =
      elementRect.top >= viewportTop + TABS_HEIGHT &&
      elementRect.bottom <= viewportBottom

    // Scroll to section with offset for tabs
    if (scrollContainer) {
      const scrollTop = scrollContainer.scrollTop
      const containerTop = containerRect?.top ?? 0
      const targetScroll =
        scrollTop + elementRect.top - containerTop - TABS_HEIGHT - 8

      scrollContainer.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth',
      })
    } else {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // Apply highlight: immediately if in view, with delay if scrolling
    const highlightDelay = isInView ? 0 : HIGHLIGHT_SCROLL_DELAY
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedSectionId(sectionId)

      // Clear scroll lock after smooth scroll completes
      isProgrammaticScrolling.current = false

      // Auto-remove highlight after duration
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightedSectionId(null)
      }, HIGHLIGHT_DURATION)
    }, highlightDelay)
  }, [])

  const value = useMemo(
    () => ({
      recipientId,
      recipientHash,
      recipientLevel,
      profile: initialProfile,
      registerChartRef,
      chartRefs,
      highlightedSectionId,
      scrollToSection,
      setScrollContainer,
      isProgrammaticScrolling,
    }),
    [
      recipientId,
      recipientHash,
      recipientLevel,
      initialProfile,
      registerChartRef,
      highlightedSectionId,
      scrollToSection,
      setScrollContainer,
    ],
  )

  return (
    <RecipientDetailContext.Provider value={value}>
      {children}
    </RecipientDetailContext.Provider>
  )
}

/**
 * Hook to check if a specific section is highlighted.
 * Use in section components that need to apply highlight styling.
 * Safe to use outside RecipientDetail context (returns false).
 */
export function useSectionHighlight(sectionId: string): boolean {
  const context = useContext(RecipientDetailContext)
  if (!context) return false
  return context.highlightedSectionId === sectionId
}
